import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";
import verified from "../../assets/images/verified.png";
import Export from "../../assets/svgs/export.svg";
import useAuth from "../../hooks/useAuth";
import { BASE_URL } from "../../utils/Element";
import Modal from "../Modal/Modal";
import ProgressBar from "../ProgressBar";

const ExportModal = ({
  showModal,
  onClose,
  exportCsvData,
  from,
  selectedData,
  onCallApi,
  advanceFilterData = [], // Only Advance Filters
  type = "deal",
  exportApiUrl,
}) => {
  const [config] = useAuth();
  const [slider, setSlider] = useState(false);
  const [disable, setDisable] = useState(false);

  const userType = useSelector((state) => state.userType);

  // Safeguard for export type
  const safeType =
    typeof type === "string" ? type : type?.type || type?.value || "deal";

  const isContractToClose =
    from === "contract-to-close" || from === "contract_to_close";

  // Dynamic columns based on module for the design mockup
  const availableColumns = {
    contact: [
      "First Name",
      "Last Name",
      "Birthday",
      "First Deal Anniversary",
      "Work Phone ",
      "Work Phone 2",
      "Home Phone ",
      "Mobile Phone ",
      "Mobile Phone 2",
      "Other",
      "Fax",
      "Personal Email",
      "Work Email",
      "Other Email",
      "Work - Address",
      "Work - Address Line 2",
      "Work - City",
      "Work - State",
      "Work - Zip code",
      "Home - Address",
      "Home - Address Line 2",
      "Home - City",
      "Home - State",
      "Home - Zip code",
      "Other - Address",
      "Other - Address Line 2",
      "Other - City",
      "Other - State",
      "Other - Zip code",
      "Company name",
      "Title",
      "Tags",
      "Lead Source",
      "Facebook Link",
      "Linkedin Link",
      "Google Drive Link",
      "Website Link",
      "Description",
    ],
    company: [
      "Company Name",
      "Phone",
      "Street Address",
      "State",
      "City",
      "Zipcode",
      "Tags",
      "Tax Record Sent",
      "Website Link",
      "Comment",
    ],
    deal: ["Deal Name", "Amount", "Stage", "Close Date", "Owner"],
    property: [
      "Property Name",
      "Street Address",
      "City",
      "State",
      "Zipcode",
      "Store",
      "property_type",
      "Lease Type",
      "Annual Rent",
      "Asking Cap Rate",
      "Asking Price",
      "Lease Commencement Date",
      "Lease Expiration Date",
      "Owner Type",
      "Owner Name",
      "Owner Phone",
      "Owner Email",
      "Owner Street Address",
      "Owner City",
      "Owner State",
      "Owner Zipcode",
      "Owner Tags",
      "Building Size",
      "Land Size",
      "Google Map Link",
      "Comment",
      "Property Tags",
    ],
    buyer: [
      "Contact Name",
      "Property Type",
      "Min Asking Price",
      "Max Asking Price",
      "Min Asking Cap Rate",
      "Min Lease Term Remaining",
      "Landlord Responsibilities",
      "States",
      "Comments",
      "Tenant Name",
      "Availability Status",
      "Acquisition Criteria Update Date",
    ],
    "contract-to-close":
      safeType === "DealMetrics"
        ? [
            "Deal ID",
            "Property Name",
            "Property Address",
            "City",
            "State",
            "Stage",
            "Lease Type",
            "Lease Term Remaining (Years)",
            "NOI",
            "Contract Price",
            "Contract Cap Rate",
            "% of Asking Price",
            "Created Date",
          ]
        : [
            "Deal ID",
            "Property",
            "Property Address",
            "City",
            "State",
            "Zipcode",
            "Status",
            "Side",
            "Primary Advisor",
            "Advisors",
            "Advisors Commissions",
            "Acquisition Name",
            "Disposition Name",
            "Contract Price",
            "GCI to Company",
            "Buyer Broker GCI",
            "Buyer BDS GCI",
            "Seller Broker GCI",
            "Seller BDS GCI",
            "Executed LOI Date",
            "Contract Date",
            "Expected Close Date",
            "Closed Date",
            "Deal Type",
            "Lost Date",
          ],
    activity: [
      "User",
      "Module",
      "Interaction Type",
      "Contact",
      "Description",
      "Date & Time"
    ],
  };

  // Also map 'contract_to_close' to the same array
  availableColumns.contract_to_close = availableColumns["contract-to-close"];

  const baseColumns = availableColumns[from] || availableColumns.contact;
  const columns = [...baseColumns].sort((a, b) => a.localeCompare(b));
  const [selectedColumns, setSelectedColumns] = useState(columns); // default all selected

  const exportType = isContractToClose ? safeType : from || "deal";

  const fileName = isContractToClose
    ? `${from}-${exportType}`
    : `${from || "export"}`;

  const sliderhandler = () => {
    setSlider(true);
  };

  const handleSelectedDownloadCSV = async () => {
    try {
      const csvContent =
        "data:text/csv;charset=utf-8," +
        exportCsvData?.map((e) => e.join(",")).join("\n");

      const url = encodeURI(csvContent);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      onCallApi?.();
      onClose();
    } catch (error) {
      console.error("Selected download error:", error);
    }
  };

  const handleDownloadCSV = () => {
    setDisable(true);

    let advanceQuery = "";

    // Build Advance Filters Query
    if (advanceFilterData?.length > 0) {
      const validAdvanceFilters = advanceFilterData.filter(
        (group) =>
          group?.filters && group.filters.some((f) => f.field && f.operator),
      );

      if (validAdvanceFilters.length > 0) {
        advanceQuery = `&advance_filters=${encodeURIComponent(JSON.stringify(validAdvanceFilters))}`;
      }
    }

    // Build API URL
    const apiUrl = exportApiUrl
      ? exportApiUrl
      : `${BASE_URL}/export-Contacts?user_id=${userType}&format=csv&type=${exportType}${advanceQuery}`;

    axios
      .get(apiUrl, {
        ...config,
        params: {
          selected_columns: selectedColumns,
        },
        responseType: "blob",
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${fileName}-${Date.now()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        onCallApi?.();
      })
      .catch((error) => {
        console.error("Error downloading CSV:", error);
      })
      .finally(() => {
        setDisable(false);
      });
  };

  const handleDownload = () => {
    if (selectedData?.length > 0) {
      handleSelectedDownloadCSV();
    } else {
      handleDownloadCSV();
    }
  };

  return (
    <Modal
      title="Export File"
      width="600px"
      desc="Easily export your data for backup or analysis purposes"
      show={showModal}
      onClose={onClose}
      className="z-[99999]"
    >
      <div className="relative z-10">
        {!slider && (
          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <p className="head-5 dark-H">Select columns to export:</p>
              <button
                className="text-sm hover:underline"
                style={{ color: "#2d5b30" }}
                onClick={() =>
                  setSelectedColumns(
                    selectedColumns.length === columns.length ? [] : columns,
                  )
                }
              >
                {selectedColumns.length === columns.length
                  ? "Deselect All"
                  : "Select All"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[160px] overflow-y-auto border border-gray-200 p-4 rounded-xl bg-gray-50/50">
              {columns.map((col) => (
                <label
                  key={col}
                  className="flex items-start gap-2 cursor-pointer min-w-0"
                >
                  <input
                    type="checkbox"
                    className="w-[18px] h-[18px] rounded cursor-pointer shrink-0 mt-0.5"
                    style={{ accentColor: "#2d5b30" }}
                    checked={selectedColumns.includes(col)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedColumns([...selectedColumns, col]);
                      } else {
                        setSelectedColumns(
                          selectedColumns.filter((c) => c !== col),
                        );
                      }
                    }}
                  />
                  <span className="body-N dark-M select-none break-words whitespace-normal leading-tight flex-1 min-w-0 pr-1">
                    {col}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="border-dashed border-2 green-b text-center p-8 rounded-xl bg-white">
          {slider ? (
            <img src={verified} alt="success" className="mx-auto" />
          ) : (
            <img src={Export} alt="export" className="mx-auto w-12 h-12 mb-2" />
          )}

          <p className="mt-2 mb-4 green-H font-medium">
            {fileName}.csv{" "}
            <span className="dark-M body-S block mt-1">
              {slider
                ? "Status: Complete (100%)"
                : `Exporting ${selectedColumns.length} columns`}
            </span>
          </p>

          <ProgressBar width={slider ? "100" : "0"} />
        </div>
      </div>

      <div className="mt-6 relative z-10">
        {slider ? (
          <button
            disabled={disable}
            className="save-button light-L head-5 w-30 green-bg-H"
            onClick={handleDownload}
          >
            Download
          </button>
        ) : (
          <>
            <button
              className="save-button light-L head-5 w-30 green-bg-H"
              onClick={sliderhandler}
            >
              Get File
            </button>
            <button onClick={onClose} className="green-H ml-5">
              Cancel
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ExportModal;
